/**
 * AdminEventsPanel — operator-only tool to inject real-world economic
 * events into the shared multiplayer world.
 *
 * See docs/proposals/multiplayer-realtime-world.md, "Admin: real-world
 * event injection" (Phase 9, the last phase of the server-authoritative
 * multiplayer conversion).
 *
 * Deliberately kept separate from src/ui/panels/AdminPanel.js (the route
 * waypoint editor) — the only thing the two admin tools share is being
 * gated on isAdmin. This is an internal tool for Peter, not player-facing
 * polish, so it stays visually simple and reuses existing modal/form/button
 * classes rather than inventing new visual language.
 *
 * Two modes:
 *   - Catalog: pick one of the existing WORLD_EVENTS (fetched from the
 *     server so this file never has to duplicate src/config/events.js),
 *     optionally override its duration, and trigger it immediately
 *     (skips the weighted random roll and 3-7 day wait).
 *   - Custom: author a one-off event with its own name/description,
 *     modifiers, and duration — for real news that isn't in the catalog
 *     (e.g. "Panama Canal Drought — Restricted Transits").
 *
 * Modifiers apply to the shared GameState.globalModifiers exactly like a
 * randomly-rolled event (same shape: oilPrice, economicHealth,
 * seasonalFactor — see src/core/GameState.js / src/config/events.js).
 * Admin-injected events look identical to players as randomly-rolled ones
 * (same world:eventStarted delta, same toast) — this panel is purely the
 * trigger, not a separate player-facing code path.
 */
import { EventBus } from '../../core/EventBus.js';
import { Modal } from '../components/Modal.js';
import {
  fetchEventCatalog,
  triggerCatalogEvent,
  triggerCustomEvent
} from '../../core/ServerConnection.js';

// Authoritative modifier keys — see GameState.globalModifiers /
// src/config/events.js WORLD_EVENTS. Do not guess additional keys here.
const MODIFIER_KEYS = ['oilPrice', 'economicHealth', 'seasonalFactor', 'portHandlingTime'];

export class AdminEventsPanel {
  constructor() {
    this.modal = new Modal('admin-events-modal', { title: 'Admin: Inject World Event' });

    this._mode = 'catalog'; // 'catalog' | 'custom'
    this._catalog = [];
    this._catalogLoaded = false;
    this._selectedEventId = null;
    this._durationOverride = '';
    this._busy = false;

    this._custom = {
      name: '',
      description: '',
      modifiers: [{ key: MODIFIER_KEYS[0], value: 1.0 }],
      durationDays: 30
    };

    EventBus.on('ui:adminEventsOpen', () => this.open());
  }

  async open() {
    this.modal.open();
    if (!this._catalogLoaded) {
      this.modal.setBody('<p class="panel-empty">Loading event catalog…</p>');
      await this._loadCatalog();
    }
    this._render();
  }

  async _loadCatalog() {
    const result = await fetchEventCatalog();
    if (result && Array.isArray(result.events)) {
      this._catalog = result.events;
      if (!this._selectedEventId && this._catalog.length) {
        this._selectedEventId = this._catalog[0].id;
      }
    } else {
      this._catalog = [];
      this._notify(result?.error || 'Failed to load event catalog.', 'error');
    }
    this._catalogLoaded = true;
  }

  _notify(message, type) {
    EventBus.emit('ui:notification', { message, type });
  }

  // ---------- render ----------

  _render() {
    const isCatalog = this._mode === 'catalog';

    this.modal.setBody(`
      <div class="admin-events-panel">
        <div class="admin-events-tabs">
          <button class="btn btn-sm ${isCatalog ? 'btn-active' : ''}" data-ev-mode="catalog">Catalog Event</button>
          <button class="btn btn-sm ${!isCatalog ? 'btn-active' : ''}" data-ev-mode="custom">Custom Event</button>
        </div>
        ${isCatalog ? this._renderCatalogMode() : this._renderCustomMode()}
      </div>`);

    this._bindEvents();
  }

  _renderCatalogMode() {
    if (!this._catalog.length) {
      return '<p class="panel-empty">No catalog events available.</p>';
    }

    const selected = this._catalog.find(e => e.id === this._selectedEventId) || this._catalog[0];

    const cardsHtml = this._catalog.map(ev => {
      const active = ev.id === selected.id;
      const [minD, maxD] = Array.isArray(ev.durationDays) ? ev.durationDays : [ev.durationDays, ev.durationDays];
      return `
        <div class="admin-event-card ${active ? 'admin-event-card-active' : ''}" data-ev-select="${ev.id}">
          <div class="admin-event-card-header"><strong>${this._escape(ev.name)}</strong></div>
          <div class="admin-event-card-body">
            <p>${this._escape(ev.description || '')}</p>
            <span>${this._formatModifiers(ev.modifiers)}</span>
            <span>Default duration: ${minD}${maxD !== minD ? `–${maxD}` : ''} days</span>
          </div>
        </div>`;
    }).join('');

    return `
      <div class="admin-event-cards">${cardsHtml}</div>
      <div class="route-field">
        <label>Duration override (days, optional)</label>
        <input type="number" id="ev-duration-override" min="1" value="${this._durationOverride}" placeholder="Use event default range">
      </div>
      <div class="admin-events-footer">
        <button class="btn btn-primary" id="btn-trigger-catalog" ${this._busy ? 'disabled' : ''}>
          ${this._busy ? 'Triggering…' : 'Trigger Event'}
        </button>
      </div>`;
  }

  _renderCustomMode() {
    const modifiersHtml = this._custom.modifiers.map((m, i) => `
      <div class="admin-modifier-row">
        <select data-mod-key="${i}">
          ${MODIFIER_KEYS.map(k => `<option value="${k}" ${m.key === k ? 'selected' : ''}>${k}</option>`).join('')}
        </select>
        <input type="number" step="0.01" data-mod-value="${i}" value="${m.value}">
        <button class="btn btn-xs btn-danger" data-mod-remove="${i}" ${this._custom.modifiers.length <= 1 ? 'disabled' : ''}>×</button>
      </div>`).join('');

    return `
      <div class="route-field">
        <label>Name</label>
        <input type="text" id="ev-custom-name" maxlength="60" value="${this._escape(this._custom.name)}" placeholder="e.g. Panama Canal Drought">
      </div>
      <div class="route-field">
        <label>Description</label>
        <input type="text" id="ev-custom-desc" maxlength="200" value="${this._escape(this._custom.description)}" placeholder="Shown to players in the event toast">
      </div>
      <div class="route-field">
        <label>Modifiers</label>
        <div class="admin-modifier-rows">${modifiersHtml}</div>
        <button class="btn btn-xs" id="btn-add-modifier">+ Add Modifier</button>
      </div>
      <div class="route-field">
        <label>Duration (days)</label>
        <input type="number" id="ev-custom-duration" min="1" value="${this._custom.durationDays}">
      </div>
      <div class="admin-events-footer">
        <button class="btn btn-primary" id="btn-fire-custom" ${this._busy ? 'disabled' : ''}>
          ${this._busy ? 'Firing…' : 'Fire Custom Event'}
        </button>
      </div>`;
  }

  _formatModifiers(modifiers) {
    if (!modifiers) return '';
    return Object.entries(modifiers).map(([k, v]) => `${k} ×${v}`).join(', ');
  }

  // ---------- events ----------

  _bindEvents() {
    const el = this.modal.el;

    el.querySelectorAll('[data-ev-mode]').forEach(btn => {
      btn.addEventListener('click', () => {
        this._mode = btn.dataset.evMode;
        this._render();
      });
    });

    if (this._mode === 'catalog') {
      el.querySelectorAll('[data-ev-select]').forEach(card => {
        card.addEventListener('click', () => {
          this._selectedEventId = card.dataset.evSelect;
          this._render();
        });
      });

      el.querySelector('#ev-duration-override')?.addEventListener('input', (e) => {
        this._durationOverride = e.target.value;
      });

      el.querySelector('#btn-trigger-catalog')?.addEventListener('click', () => this._triggerCatalog());
    } else {
      el.querySelector('#ev-custom-name')?.addEventListener('input', (e) => {
        this._custom.name = e.target.value;
      });
      el.querySelector('#ev-custom-desc')?.addEventListener('input', (e) => {
        this._custom.description = e.target.value;
      });
      el.querySelector('#ev-custom-duration')?.addEventListener('input', (e) => {
        this._custom.durationDays = e.target.value;
      });

      el.querySelectorAll('[data-mod-key]').forEach(sel => {
        sel.addEventListener('change', () => {
          this._custom.modifiers[Number(sel.dataset.modKey)].key = sel.value;
        });
      });
      el.querySelectorAll('[data-mod-value]').forEach(input => {
        input.addEventListener('input', () => {
          this._custom.modifiers[Number(input.dataset.modValue)].value = input.value;
        });
      });
      el.querySelectorAll('[data-mod-remove]').forEach(btn => {
        btn.addEventListener('click', () => {
          const i = Number(btn.dataset.modRemove);
          if (this._custom.modifiers.length > 1) {
            this._custom.modifiers.splice(i, 1);
            this._render();
          }
        });
      });

      el.querySelector('#btn-add-modifier')?.addEventListener('click', () => {
        this._custom.modifiers.push({ key: MODIFIER_KEYS[0], value: 1.0 });
        this._render();
      });

      el.querySelector('#btn-fire-custom')?.addEventListener('click', () => this._triggerCustom());
    }
  }

  // ---------- actions ----------

  async _triggerCatalog() {
    if (!this._selectedEventId || this._busy) return;
    this._busy = true;
    this._render();

    const override = this._durationOverride !== '' ? Number(this._durationOverride) : undefined;
    const result = await triggerCatalogEvent(this._selectedEventId, override);

    this._busy = false;
    if (result?.success) {
      this._notify(`Event triggered: ${result.event?.name || this._selectedEventId}`, 'success');
    } else {
      this._notify(result?.error || 'Failed to trigger event.', 'error');
    }
    this._render();
  }

  async _triggerCustom() {
    if (this._busy) return;

    const name = this._custom.name.trim();
    if (!name) { this._notify('Custom event needs a name.', 'error'); return; }

    const durationDays = Number(this._custom.durationDays);
    if (!Number.isFinite(durationDays) || durationDays <= 0) {
      this._notify('Duration must be a positive number of days.', 'error');
      return;
    }

    const modifiers = {};
    for (const m of this._custom.modifiers) {
      const value = Number(m.value);
      if (!MODIFIER_KEYS.includes(m.key) || !Number.isFinite(value)) continue;
      modifiers[m.key] = value;
    }
    if (Object.keys(modifiers).length === 0) {
      this._notify('Add at least one valid modifier.', 'error');
      return;
    }

    this._busy = true;
    this._render();

    const result = await triggerCustomEvent({
      name,
      description: this._custom.description.trim(),
      modifiers,
      durationDays
    });

    this._busy = false;
    if (result?.success) {
      this._notify(`Custom event fired: ${result.event?.name || name}`, 'success');
    } else {
      this._notify(result?.error || 'Failed to fire custom event.', 'error');
    }
    this._render();
  }

  _escape(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
